/**
 * Segment definitions — the named sets a funnel can be asked about.
 *
 * Definitions only. A segment's MEMBERSHIP is never stored and is not shown
 * here: the funnel evaluates the criteria when it is asked, so what this page
 * edits is the question, not the answer.
 *
 * Two kinds, and the difference is the one thing a reader has to understand:
 * criteria over where someone came FROM select a set that only grows, because
 * the acquisition touch is frozen at their first arrival; criteria over what
 * someone DID select a different set of people every day. The second kind's
 * funnel therefore carries the date it was evaluated and how many people it
 * found, and this page says so rather than leaving it to be discovered.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSegments, listSegmentValues, createSegment, updateSegment, deleteSegment, countSegments,
  isBehavioural, acquisitionPart, describeCriteria as describe, type Segment, type SegmentCriteria,
} from '../../../api/segmentsApi';
import { Button, DataTable, InfoTip, Modal, useConfirm, type ColumnDef, type SortState } from '@shared-ui/components';
import './SegmentsPanel.css';

/**
 * The two halves are independent: a segment may use either, or both.
 *
 * `useBehaviour` is a checkbox rather than a mode, because "from Instagram AND
 * used the lab this month" is one segment, not a choice between two.
 */
type Draft = {
  id?: string;
  name: string;
  description: string;
  pairs: { field: string; value: string }[];
  useBehaviour: boolean;
  did: string;
  withinDays: string;
};

const emptyDraft = (): Draft => ({
  name: '', description: '',
  pairs: [{ field: '', value: '' }], useBehaviour: false, did: '', withinDays: '30',
});

function draftFrom(s: Segment): Draft {
  const acq = Object.entries(acquisitionPart(s.criteria));
  return {
    id: s.id, name: s.name, description: s.description ?? '',
    pairs: acq.length ? acq.map(([field, value]) => ({ field, value })) : [{ field: '', value: '' }],
    useBehaviour: isBehavioural(s.criteria),
    did: s.criteria.did ?? '',
    withinDays: String(s.criteria.withinDays ?? 30),
  };
}

function criteriaFrom(d: Draft): SegmentCriteria {
  const out: SegmentCriteria = {};
  for (const p of d.pairs) if (p.field && p.value.trim()) out[p.field] = p.value.trim();
  if (d.useBehaviour) { out.did = d.did.trim(); out.withinDays = Number(d.withinDays); }
  return out;
}

export default function SegmentsPanel() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['segments'], queryFn: listSegments });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ sortBy: 'name', sortOrder: 'asc' });
  // Its own query: the list shows at once, the counts (one scan of every
  // account) arrive after.
  const counts = useQuery({ queryKey: ['segment-counts'], queryFn: countSegments, staleTime: 60_000 });
  const { confirm, dialog } = useConfirm();
  // Only while editing: ten GROUP BYs are the editor's cost, not the list's.
  const suggestions = useQuery({
    queryKey: ['segment-values'], queryFn: listSegmentValues,
    enabled: draft !== null, staleTime: 5 * 60_000,
  });

  const done = () => {
    setDraft(null); setFailed(null);
    void qc.invalidateQueries({ queryKey: ['segments'] });
    void qc.invalidateQueries({ queryKey: ['segment-counts'] });
  };
  const fail = (e: unknown) => setFailed((e as Error).message);

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = { name: d.name.trim(), description: d.description.trim(), criteria: criteriaFrom(d) };
      return d.id ? updateSegment(d.id, body) : createSegment(body);
    },
    onSuccess: done, onError: fail,
  });
  const remove = useMutation({ mutationFn: deleteSegment, onSuccess: done, onError: fail });

  if (isLoading) return <p className="sa-empty">Loading segments…</p>;
  if (error) return <p className="sa-error">Failed: {(error as Error).message}</p>;

  const fields = data?.fields ?? [];
  const segments = data?.segments ?? [];
  const seen = suggestions.data?.values ?? {};
  const setPair = (i: number, patch: Partial<Draft['pairs'][number]>) => {
    if (!draft) return;
    const pairs = [...draft.pairs]; pairs[i] = { ...pairs[i], ...patch };
    setDraft({ ...draft, pairs });
  };

  const columns: ColumnDef<Segment>[] = [
    {
      key: 'name', header: 'Name', sortable: true, sortValue: seg => seg.name.toLowerCase(), mobile: 'title',
      // At most half the table: a long description otherwise squeezes the
      // criteria into a narrow column that wraps mid-value.
      width: '50%',
      render: seg => (
        <>
          {seg.name}
          {seg.description && <div className="seg-desc">{seg.description}</div>}
        </>
      ),
    },
    { key: 'criteria', header: 'Criteria', render: seg => <code>{describe(seg.criteria)}</code> },
    {
      key: 'people', className: 'sa-num', sortable: true,
      header: (
        <span>
          People
          <InfoTip text={'Accounts whose first touch matches the segment now — and, for a "did" clause, who did that within its days. Internal and test accounts left out. The same count the "what a segment\'s people did" chart uses, over all time. — where a criterion (appSource) is not a property of a person.'
            + (counts.data ? ` Worked out ${new Date(counts.data.evaluatedAt).toLocaleString()}.` : '')} />
        </span>
      ),
      sortValue: seg => counts.data?.counts[seg.id] ?? -1,
      render: seg => {
        if (counts.isLoading) return <span className="sa-zero">…</span>;
        const n = counts.data?.counts[seg.id];
        return n === null || n === undefined ? <span className="sa-zero">—</span> : n.toLocaleString();
      },
    },
    {
      key: 'kind', header: 'Kind', sortable: true, sortValue: seg => (isBehavioural(seg.criteria) ? 1 : 0), mobile: 'accessory',
      render: seg => (isBehavioural(seg.criteria)
        ? <span className="seg-kind is-moving" title="Names behaviour, so membership changes daily — its funnel carries an evaluated-at date">moves</span>
        : <span className="seg-kind" title="Defined only on the frozen acquisition touch, so membership only grows">stable</span>),
    },
  ];

  return (
    <section className="sa-panel seg">
      <div className="seg-head">
        <h2>
          Segments
          <InfoTip>
            <span className="sa-tip-p">
              A segment is a named set defined by criteria. Only the criteria are stored — membership is
              worked out each time a funnel is asked, so a segment can never drift from its own definition.
            </span>
          </InfoTip>
        </h2>
        <Button variant="secondary" size="sm" onClick={() => { setFailed(null); setDraft(emptyDraft()); }}>New segment</Button>
      </div>

      <DataTable<Segment>
        columns={columns}
        data={segments}
        rowKey={seg => seg.id}
        sort={sort}
        onSortChange={setSort}
        onRowClick={seg => { setFailed(null); setDraft(draftFrom(seg)); }}
        compact
        emptyMessage="No segments yet — New segment makes one."
      />

      <Modal isOpen={!!draft} onClose={() => { setDraft(null); setFailed(null); }}
        title={draft?.id ? `Edit segment — ${draft.name}` : 'New segment'} size="lg">
        {draft && (
    <div className="seg-form">
              <div className="seg-grid">
                <label className="seg-field">
                  <span>Name</span>
                  <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Instagram, October push" />
                </label>
                <label className="seg-field">
                  <span>Description <em>optional</em></span>
                  <input value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} />
                </label>
              </div>

              <fieldset className="seg-group">
                <legend>Where they came from</legend>
                {draft.pairs.map((p, i) => {
                  const options = p.field ? seen[p.field] ?? [] : [];
                  return (
                    <div className="seg-pair" key={i}>
                      <select aria-label="Field" value={p.field} onChange={e => setPair(i, { field: e.target.value })}>
                        <option value="">Choose a field…</option>
                        {fields.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <span className="seg-eq">=</span>
                      {/* A datalist, not a select: the recent values are offered,
                          and anything else can still be typed — a campaign about
                          to launch has no data yet. */}
                      <input aria-label="Value" list={`seg-values-${i}`} value={p.value}
                        disabled={!p.field}
                        placeholder={!p.field ? 'choose a field first'
                          : options.length ? 'pick or type a value' : 'type a value'}
                        onChange={e => setPair(i, { value: e.target.value })} />
                      <datalist id={`seg-values-${i}`}>
                        {options.map(v => <option key={v} value={v} />)}
                      </datalist>
                      {draft.pairs.length > 1
                        ? <button className="seg-remove" aria-label="Remove this field" onClick={() => setDraft({
                            ...draft, pairs: draft.pairs.filter((_, j) => j !== i),
                          })}>✕</button>
                        : <span />}
                      {p.field && (
                        <span className="seg-hint">
                          {suggestions.isLoading ? 'Loading recent values…'
                            : options.length
                              ? `${options.length} value${options.length === 1 ? '' : 's'} seen in the last ${suggestions.data?.days ?? 30} days`
                              : `No values seen in the last ${suggestions.data?.days ?? 30} days — type one`}
                        </span>
                      )}
                    </div>
                  );
                })}
                <button className="seg-add" onClick={() => setDraft({
                  ...draft, pairs: [...draft.pairs, { field: '', value: '' }],
                })}>+ Add another field</button>
                {/* Every added field NARROWS the set. Said plainly because the
                    opposite reading — that more fields find more people — is the
                    natural one and produces a segment nobody is in. */}
                <p className="sa-note">All fields must match. Each one you add makes the segment smaller.</p>
              </fieldset>

              <fieldset className="seg-group">
                <legend>
                  <label className="seg-check">
                    <input type="checkbox" checked={draft.useBehaviour}
                      onChange={e => setDraft({ ...draft, useBehaviour: e.target.checked })} />
                    …and what they did
                  </label>
                </legend>
                {draft.useBehaviour && (
                  <>
                    <div className="seg-grid">
                      <label className="seg-field">
                        <span>Did</span>
                        <input list="seg-did" value={draft.did} placeholder="pick or type an act"
                          onChange={e => setDraft({ ...draft, did: e.target.value })} />
                        <datalist id="seg-did">
                          {(suggestions.data?.did ?? []).map(k => <option key={k} value={k} />)}
                        </datalist>
                      </label>
                      <label className="seg-field seg-narrow">
                        <span>Within days</span>
                        <input type="number" min={1} max={365} value={draft.withinDays}
                          onChange={e => setDraft({ ...draft, withinDays: e.target.value })} />
                      </label>
                    </div>
                    <p className="sa-note">
                      Adding this makes the segment <strong>move</strong>: it selects a different set of
                      people every day, and no acquisition field can hold it still. Its funnel reports
                      the date it was evaluated and how many people it found — a change in that number is
                      a change in <em>who</em> was in the set, not in what they did.
                    </p>
                  </>
                )}
              </fieldset>

              {failed && <p className="sa-error">{failed}</p>}

              <div className="seg-actions">
                {/* Apart from Save, and on the other side: the one action here that
                    cannot be undone by editing the form back. */}
                {draft.id && (
                  <Button variant="danger" size="sm" disabled={remove.isPending} className="seg-delete"
                    onClick={async () => {
                      if (await confirm({
                        message: <>Delete the segment <strong>{draft.name}</strong>? Reports that use it lose it; nothing about its people changes.</>,
                        confirmLabel: 'Delete', confirmVariant: 'danger',
                      })) remove.mutate(draft.id!);
                    }}>
                    Delete
                  </Button>
                )}
                <Button variant="secondary" size="sm" onClick={() => { setDraft(null); setFailed(null); }}>Cancel</Button>
                <Button variant="primary" size="sm" disabled={save.isPending || !draft.name.trim()} onClick={() => save.mutate(draft)}>
                  {save.isPending ? 'Saving…' : draft.id ? 'Save' : 'Create'}
                </Button>
              </div>
            </div>
        )}
      </Modal>
      {dialog}
    </section>
  );
}
